import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useLoaderData, Link } from "@remix-run/react";
import { Trophy } from "lucide-react";
import { getAuthenticatedUser } from "~/services/auth.server";
import { getAllPublicLeagues } from "~/services/league/league.server";
import PageWithNavigation from "~/components/PageWithNavigation";
import { defaultMetaTagsBuilder } from "~/utils/meta";

export const meta: MetaFunction = ({ location }) => {
  const url = new URL(location.pathname, "https://app.7qz.io");
  return defaultMetaTagsBuilder({
    title: "Leaderboard",
    description: "View live leaderboards for all active 7Qz games",
    url: url,
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthenticatedUser(request);
  const allPublicLeagues = await getAllPublicLeagues();

  const activeLeagues =
    allPublicLeagues.success && allPublicLeagues.payload
      ? allPublicLeagues.payload.filter((league) =>
          league.closing_date_time
            ? league.closing_date_time.getTime() >= new Date().getTime()
            : false
        )
      : [];

  return { user, activeLeagues };
}

export default function LeaderboardIndex() {
  const { user, activeLeagues } = useLoaderData<typeof loader>();

  return (
    <PageWithNavigation user={user}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px" }}>
        <h1
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 28,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".03em",
            color: "#f0f0f2",
            marginBottom: 20,
          }}
        >
          Leaderboard
        </h1>

        {activeLeagues.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {activeLeagues.map((league) => (
              <Link
                key={league.id}
                to={`/leaderboard/league/${league.id}`}
                style={{ textDecoration: "none" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 16px",
                    background: "#141416",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 10,
                    transition: "border-color .15s",
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.borderColor =
                      "rgba(0,197,160,0.35)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.borderColor =
                      "rgba(255,255,255,0.07)")
                  }
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      overflow: "hidden",
                      flexShrink: 0,
                      background: "rgba(0,197,160,0.10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {league.image_url ? (
                      <img
                        src={`https://app.7qz.io/cdn-cgi/image/fit=cover,width=80,height=80,format=auto/${league.image_url}`}
                        alt={league.label}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Trophy size={18} color="#00c5a0" />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: "'Barlow', sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#f0f0f2",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {league.label}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Barlow', sans-serif",
                        fontSize: 12,
                        color: "#8a8a95",
                        marginTop: 2,
                      }}
                    >
                      {league._count.bets} player{league._count.bets !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#00c5a0",
                      fontFamily: "'Barlow', sans-serif",
                    }}
                  >
                    View →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "0 20px",
              height: 80,
              border: "1px dashed rgba(0,197,160,0.22)",
              borderRadius: 14,
              background: "linear-gradient(135deg, rgba(0,197,160,0.04) 0%, transparent 60%)",
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,197,160,0.12)",
                border: "1px solid rgba(0,197,160,0.22)",
                color: "#00c5a0",
              }}
            >
              <Trophy size={20} strokeWidth={1.5} />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#f0f0f2",
                  lineHeight: 1.2,
                }}
              >
                No leaderboards available right now
              </div>
              <div
                style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: 12,
                  color: "#8a8a95",
                  marginTop: 2,
                }}
              >
                Check back when a live game is running.
              </div>
            </div>
          </div>
        )}
      </div>
    </PageWithNavigation>
  );
}
